import { mergeObjects } from '@origin_js/helper';
import ComponentsObserver from '@origin_js/components-observer';
import HttpRequest from '@origin_js/plugins/http-request';

/**
 * Default options for OnboardingGuide initialization.
 * @type {Object}
 */
const defaultOptions = {
    name: 'OnboardingGuide',
    selectors: {
        element: '.x-variants_onboarding-guide',
        form: 'form',
        requiredFields: '[required]',
        submitButton: 'button[type="submit"]',
        backButton: '.back-button',
        nextButton: '.next-button',
        finishButton: '.finish-button',
        modal: '.x-base_modal',
        checkbox: '.x-base_forms_checkbox',
        stepContent: '.step-content'
    },
    classes: {
        invalid: 'invalid',
        active: 'is-active',
        error: 'text-red-500 text-sm mt-1'
    },
};

/**
 * Class representing an OnboardingGuide component.
 */
class OnboardingGuide {
    /**
     * Constructor for the OnboardingGuide
     * @param {HTMLElement} element - OnboardingGuide element
     * @param {Object} [options] - Options for the OnboardingGuide
     */
    constructor (element, options = {}) {
        this.element = element;
        this.options = mergeObjects(defaultOptions, options);
        this.currentStep = parseInt(options.currentStep) || 1;
        this.totalSteps = parseInt(options.totalSteps) || 1;
        this.steps = this.element.querySelectorAll(this.options.selectors.stepContent);
        this.init();
    }

    /**
     * Initializes the OnboardingGuide
     */
    init () {
        this.registerEventListener();
        this.updateNavigationButtons();
    }

    /**
     * Registers event listeners
     */
    registerEventListener () {
        const nextButton = this.element.querySelector(this.options.selectors.nextButton);
        if (nextButton) {
            nextButton.addEventListener('click', async (event) => {
                event.preventDefault();
                await this.handleNextStep(event);
            });
        }

        const backButton = this.element.querySelector(this.options.selectors.backButton);
        if (backButton) {
            backButton.addEventListener('click', (event) => {
                event.preventDefault();
                this.handlePreviousStep();
            });
        }

        const finishButton = this.element.querySelector(this.options.selectors.finishButton);
        if (finishButton) {
            finishButton.addEventListener('click', async (event) => {
                event.preventDefault();
                await this.handleFinish(event);
            });
        }
    }

    /**
     * Handles the click on the "Next"-Button
     * @param {Event} event
     */
    async handleNextStep (event) {
        const currentStepElement = this.getCurrentStep();

        if (currentStepElement.dataset.isForm === 'true') {
            if (!this.validateCurrentStep()) {
                return;
            }

            try {
                await this.submitStepData();
            } catch (error) {
                console.error('Fehler beim Senden der Daten:', error);
                return;
            }
        }

        this.showStep(this.currentStep + 1);
    }

    /**
     * Handles the click on the "Back"-Button
     */
    handlePreviousStep () {
        this.showStep(this.currentStep - 1);
    }

    async handleFinish (event) {
        const currentStepElement = this.getCurrentStep();

        if (currentStepElement.dataset.isForm === 'true') {
            if (!this.validateCurrentStep()) {
                return;
            }

            try {
                await this.submitStepData();
                this.closeModal();
            } catch (error) {
                console.error('Fehler beim Senden der Daten:', error);
            }
        } else {
            this.closeModal();
        }
    }

    /**
     * Closes the modal
     */
    closeModal () {
        const modal = this.element.querySelector(this.options.selectors.modal);
        if (modal) {
            const clickEvent = new MouseEvent('click', {
                bubbles: true,
                cancelable: true,
                view: window
            });

            modal.setAttribute('data-close-modal', modal.id);
            modal.dispatchEvent(clickEvent);
            modal.removeAttribute('data-close-modal');
        }
    }

    /**
     * Validates all fields of the current step
     * @returns {boolean} True if all fields are valid, false otherwise
     */
    validateCurrentStep () {
        const currentStep = this.getCurrentStep();
        const checkboxElements = currentStep.querySelectorAll(this.options.selectors.checkbox);

        let isValid = true;
        checkboxElements.forEach(checkboxElement => {
            const input = checkboxElement.querySelector('input[required]');
            if (input && !input.checked) {
                isValid = false;
                this.showError(checkboxElement);
            } else {
                this.hideError(checkboxElement);
            }
        });

        return isValid;
    }

    /**
     * Show error message for a field
     * @param {HTMLElement} field - field to show error message for
     */
    showError (field) {
        field.classList.add(this.options.classes.invalid);
    }

    /**
     * Remove error message for a field
     * @param {HTMLElement} field - field to remove error message for
     */
    hideError (field) {
        field.classList.remove(this.options.classes.invalid);
    }

    /**
     * send data of the current step to the server
     * @returns {Promise} promise that resolves with the server response
     */
    async submitStepData () {
        const currentStep = this.getCurrentStep();
        const stepData = this.collectStepData(currentStep);
        const endpoint = currentStep.dataset.endpoint;

        if (!endpoint) {
            console.warn('No Endpoint defined for this step');
            return;
        }

        try {
            const request = new HttpRequest({
                endpoint,
                showSuccessMessage: false
            });

            const [response] = await Promise.all([
                request.post(stepData)
            ]);

            if (response.success) {
                return response.payload;
            } else {
                throw new Error('Server error');
            }
        } catch (error) {
            console.error('Fehler beim Senden der Daten:', error);
            throw error;
        }
    }

    /**
     * collects the data of the current step
     * @param {HTMLElement} step - current step element
     * @returns {Object} collected data
     */
    collectStepData (step) {
        const formData = {};
        const inputs = step.querySelectorAll('input');

        inputs.forEach(input => {
            if (input.type === 'checkbox') {
                formData[input.name] = input.checked;
            } else {
                formData[input.name] = input.value;
            }
        });

        return formData;
    }

    /**
     * returns the current step element
     * @returns {HTMLElement} current step element
     */
    getCurrentStep () {
        return this.steps[this.currentStep - 1];
    }

    /**
     * show the step with the given index
     * @param {number} stepIndex - index of the step to show
     */
    showStep (stepIndex) {
        this.getCurrentStep().style.display = 'none';
        this.currentStep = stepIndex;
        this.getCurrentStep().style.display = 'block';
        this.updateNavigationButtons();
    }

    /**
     * refreshes the visibility of the navigation buttons
     */
    updateNavigationButtons () {
        const backButton = this.element.querySelector(this.options.selectors.backButton);
        const nextButton = this.element.querySelector(this.options.selectors.nextButton);
        const finishButton = this.element.querySelector(this.options.selectors.finishButton);

        if (backButton) {
            backButton.style.display = this.currentStep > 1 ? 'inline-block' : 'none';
        }
        if (nextButton) {
            nextButton.style.display = this.currentStep < this.totalSteps ? 'inline-block' : 'none';
        }
        if (finishButton) {
            finishButton.style.display = this.currentStep === this.totalSteps ? 'inline-block' : 'none';
        }
    }
}

/**
 * Initializes an OnboardingGuide element
 * @param {Element} el - DOM Element representing the OnboardingGuide
 * @returns {OnboardingGuide} OnboardingGuide instance
 */
function initializeOnboardingGuideElement (el) {
    console.log(`### Init ${defaultOptions.name} ###`);
    const options = el.dataset.options ? JSON.parse(el.dataset.options) : {};
    return new OnboardingGuide(el, options);
}

/**
 * Initializes OnboardingGuide instances
 */
function initOnboardingGuides () {
    const observer = new ComponentsObserver({
        selector: defaultOptions.selectors.element,
        callback: initializeOnboardingGuideElement,
    });
    observer.init();
}

export default initOnboardingGuides();
